import { useMemo, useState } from 'react';
import ActivityGroupEditDetailsForm from './ActivityGroupEditDetailsForm';
import {
    Form,
    Button,
    Header,
    SpaceBetween,
    Modal,
    Box,
    TextContent,
    StatusIndicator,
    Alert,
} from '@amzn/awsui-components-react-v3';
import { useHistory } from 'react-router-dom';
import scheduleManagementApi from '../../../../../api/scheduleManagementApi';
import CancelModal, {
    CancelModalProps,
} from '../../../../../../common/components/CancelModal/CancelModal';
import { createValidations, updateValidations } from '../../utils/validations';
import { useActivityGroup } from '../../hooks/hooks';
import { DeliverySession } from '../../../../../interfaces/activity';
import { ActivityStatus } from '../../../Common/Common';
import FormActions from '../FormActions/FormActions';

const ActivityGroupEditDetails = ({
    originalStatus,
}: {
    originalStatus: ActivityStatus;
}) => {
    const history = useHistory();
    const activityGroup = useActivityGroup();

    const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
    const [isOneWayModalVisible, setIsOneWayModalVisible] = useState(false);
    const [isAcknowledgeButtonSubmitting, setIsAcknowledgeButtonSubmitting] =
        useState(false);
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

    const cancelModalProps: CancelModalProps = {
        isCancelModalVisible,
        setIsCancelModalVisible,
        onCancelConfirm: () => {
            history.push({
                pathname: activityGroup.pk
                    ? `/activities/group/${activityGroup.pk}`
                    : `/activities/group/list`,
            });
        },
        testPrefix: 'EditActivityGroup',
    };

    const [saveDisabled, saveDisabledReason] = useMemo(() => {
        const validations = activityGroup.pk
            ? {
                  ...createValidations,
                  activityGroupTimeRange:
                      updateValidations.activityGroupTimeRange,
              }
            : createValidations;

        let errorMessage = '';

        // Check validations to show in the UI
        for (const validation of Object.keys(validations)) {
            if (!validations[validation].isValid(activityGroup)) {
                errorMessage = validations[validation].invalidMessage;

                if (validation === 'activityGroupTimeRange') {
                    if (hasAttemptedSubmit) {
                        setErrorMessage(errorMessage);
                    }
                } else {
                    // Capture the message
                    if (hasAttemptedSubmit) {
                        setErrorMessage(errorMessage);
                    }
                }
            }
        }

        if (hasAttemptedSubmit && !errorMessage) {
            setErrorMessage('');
        }
        // Always return false for saveDisabled to keep button enabled
        return [false, errorMessage];
    }, [activityGroup, hasAttemptedSubmit]);

    const checkForAcknowledgment = () => {
        setHasAttemptedSubmit(true);
        setIsFormSubmitting(true);

        // First, check the date range validation (highest priority)
        if (!createValidations.inValidateDateRange.isValid(activityGroup)) {
            setErrorMessage(
                createValidations.inValidateDateRange.invalidMessage,
            );
            setIsFormSubmitting(false);
            return;
        }

        // Then proceed with other validations
        const validations = activityGroup.pk
            ? {
                  ...createValidations,
                  activityGroupTimeRange:
                      updateValidations.activityGroupTimeRange,
              }
            : createValidations;

        // Check all validations and collect errors
        let hasValidationErrors = false;
        let errorMessages = [];

        for (const validation of Object.keys(validations)) {
            // Skip the date range validation as we already checked it
            if (validation === 'inValidateDateRange') continue;

            try {
                if (!validations[validation].isValid(activityGroup)) {
                    hasValidationErrors = true;
                    errorMessages.push(validations[validation].invalidMessage);
                }
            } catch (error) {
                hasValidationErrors = true;
                errorMessages.push(`Error in validation: ${error.message}`);
            }
        }

        // If there are validation errors, show them and stop submission
        if (hasValidationErrors) {
            setErrorMessage(errorMessages[0]); // Show the first error message
            setIsFormSubmitting(false);
            return;
        }

        if (
            originalStatus !== activityGroup.status &&
            [
                ActivityStatus.Canceled.valueOf(),
                ActivityStatus.Active.valueOf(),
            ].includes(activityGroup.status)
        ) {
            setIsOneWayModalVisible(true);
        } else {
            handleClickSaveButton();
        }
    };

    const handleClickSaveButton = async () => {
        try {
            const activityGroupDetails = {
                name: activityGroup.name,
                display_name: activityGroup.name,
                active: true,
                status: activityGroup.status,
                program_name: activityGroup.program_name,
                catalog_item_id: activityGroup.catalog_item_id,
                catalog_item_versioned_id:
                    activityGroup.catalog_item_versioned_id,
                start_timestamp: activityGroup.start_timestamp,
                end_timestamp: activityGroup.end_timestamp,
            };
            await scheduleManagementApi.putActivityGroup(activityGroupDetails);

            await Promise.all(
                activityGroup.child_activities.map(async (activity) => {
                    const {
                        pk,
                        delivery_name,
                        option_number,
                        delivery_sessions,
                        ...other_activity_properties
                    } = activity;

                    const truncated_sessions: DeliverySession[] = [];
                    if (delivery_sessions?.length > 0) {
                        for (const session of delivery_sessions) {
                            if (session.v_ilt_info) {
                                const {
                                    url,
                                    recording_url,
                                    ...truncated_v_ilt_info
                                } = session.v_ilt_info;
                                truncated_sessions.push({
                                    ...session,
                                    v_ilt_info: truncated_v_ilt_info,
                                });
                            } else {
                                truncated_sessions.push(session);
                            }
                        }
                    }

                    const filteredActivity = {
                        ...other_activity_properties,
                        delivery_sessions: truncated_sessions,
                    };

                    if (pk) {
                        return scheduleManagementApi.updateActivityById({
                            id: pk,
                            activity: filteredActivity,
                        });
                    }
                }),
            );

            await new Promise((resolve) => setTimeout(resolve, 500));
            history.push({
                pathname: `/activities/group/${activityGroup.pk || activityGroup.name}/`,
            });
        } catch (error) {
            setErrorMessage('An unexpected error occurred while saving.');
        } finally {
            setIsFormSubmitting(false);
            setIsAcknowledgeButtonSubmitting(false);
        }
    };

    if (activityGroup.is_loading) {
        return (
            <StatusIndicator type="loading">
                Loading Activity Group...
            </StatusIndicator>
        );
    }
    return (
        <Form
            header={
                <Header variant="h1">
                    {activityGroup.pk
                        ? activityGroup.display_name || activityGroup.name
                        : 'Add activity group'}
                </Header>
            }
            actions={
                <FormActions
                    isSubmitting={isFormSubmitting}
                    isSaveDisabled={saveDisabled}
                    saveDisabledReason={saveDisabledReason}
                    setIsCancelModalVisible={setIsCancelModalVisible}
                    onSave={checkForAcknowledgment}
                />
            }
        >
            {hasAttemptedSubmit && errorMessage && (
                <Alert type="error" header="Error">
                    {errorMessage}
                </Alert>
            )}
            {
                <ActivityGroupEditDetailsForm
                    mode={activityGroup.pk ? 'EDIT' : 'CREATE'}
                />
            }
            <CancelModal {...cancelModalProps} />
            <Modal
                data-testid="activity-group-one-way-status-change"
                header="One-Way Status Change Detected"
                onDismiss={() => {
                    setIsFormSubmitting(false);
                    setIsOneWayModalVisible(false);
                }}
                visible={isOneWayModalVisible}
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button
                                disabled={isAcknowledgeButtonSubmitting}
                                variant="normal"
                                onClick={() => {
                                    setIsFormSubmitting(false);
                                    setIsOneWayModalVisible(false);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                disabled={isAcknowledgeButtonSubmitting}
                                loading={isAcknowledgeButtonSubmitting}
                                variant="primary"
                                onClick={() => {
                                    setIsAcknowledgeButtonSubmitting(true);
                                    setIsOneWayModalVisible(false);
                                    handleClickSaveButton();
                                }}
                            >
                                Acknowledge and Save
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                <TextContent>
                    {`Setting the Activity Group status to ${
                        activityGroup.status === ActivityStatus.Active
                            ? 'Active'
                            : 'Canceled'
                    } is a one-way change that will result in Activities being made ${
                        activityGroup.status === ActivityStatus.Active
                            ? 'available'
                            : 'unavailable'
                    } for registration by customers.`}
                </TextContent>
            </Modal>
        </Form>
    );
};

export default ActivityGroupEditDetails;
